import { useTenant } from '$features/auth/hooks/use-tenant'
import { Card, DisplayLabel, Switch, Text, ToastHelper } from '$components'
import { cn } from '$utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { tenantActiveQueryKey, tenantUpdateMutation } from '$api-client/@tanstack/react-query.gen'

export function SmsRemindersSettings() {
  const { tenantId, smsRemindersEnabled, smsNumber } = useTenant()
  const queryClient = useQueryClient()

  const { mutate: updateTenant, isPending: isUpdating } = useMutation({
    ...tenantUpdateMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: tenantActiveQueryKey(),
      })

      ToastHelper.success('SMS reminder settings updated.')
    },
    onError: () => ToastHelper.error('Failed to update SMS reminder settings.'),
  })

  const handleToggleChange = (enabled: boolean) => {
    if (!tenantId) {
      return
    }

    updateTenant({
      path: { id: tenantId },
      body: {
        sms_reminders_enabled: enabled,
      },
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-6">
        <div className="flex-1">
          <DisplayLabel>SMS appointment reminders</DisplayLabel>

          <Text
            type="s"
            className="text-gray-500"
          >
            Send automated text reminders to clients before their appointments. Each outbound SMS uses one credit from your balance.
          </Text>
        </div>

        <Switch
          id="sms-reminders-enabled"
          value={smsRemindersEnabled ?? false}
          onChange={handleToggleChange}
          disabled={isUpdating}
        />
      </div>

      <div>
        <DisplayLabel>SMS number</DisplayLabel>

        <Text
          type="s"
          className={cn(
            'text-gray-500',
            !smsRemindersEnabled && 'text-gray-400',
          )}
        >
          {smsNumber
            ? 'Clients receive reminders from this number.'
            : 'A dedicated number will be assigned automatically when your first booking with SMS reminders is confirmed.'}
        </Text>

        <Card
          padding="sm"
          rounding="md"
          className={cn(
            'mt-3',
            !smsRemindersEnabled && 'opacity-60',
          )}
        >
          <Text
            type="s"
            className={cn(
              'font-mono text-gray-700',
              !smsNumber && 'text-gray-500 italic font-sans',
              !smsRemindersEnabled && 'text-gray-400',
            )}
          >
            {smsNumber ?? 'Assigned automatically upon first appointment creation when enabled.'}
          </Text>
        </Card>
      </div>
    </div>
  )
}
